import { isDefined } from 'react-financial-charts';

export function rgbaConvert(rgbColors: string[], opacity: number) {
  return rgbColors?.map(color => {
    // Extract the RGB values using regex
    const rgbValues = color.match(/\d+/g);
    if (rgbValues) {
      const r = parseInt(rgbValues[0]);
      const g = parseInt(rgbValues[1]);
      const b = parseInt(rgbValues[2]);

      // Return the RGBA formatted color
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    return undefined;
  });
}

export function rgbToRgbaConvert(rgbColor: string, opacity: number): string | undefined {
  const rgbValues = rgbColor?.match(/\d+/g);
  if (rgbValues) {
    const r = parseInt(rgbValues[0]);
    const g = parseInt(rgbValues[1]);
    const b = parseInt(rgbValues[2]);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  return undefined;
}

export function solidColor(color: string) {
  return color?.replace(/[\d.]+\)$/g, '1)');
}

/**
 * Converts a hex color string with alpha (e.g., "#RRGGBBAA") to "rgba(r, g, b, a)" format.
 * @param hex The hex color string (e.g., "#46299c8c")
 * @returns The RGBA string (e.g., "rgba(70, 41, 156, 0.55)")
 */
export function hexToRgba(hex: string, opacity?: number): string {
  // Remove '#' if present
  const normalizedHex = hex.replace(/^#/, '');

  let r: number, g: number, b: number, a: number;

  if (normalizedHex.length === 8) {
    r = parseInt(normalizedHex.slice(0, 2), 16);
    g = parseInt(normalizedHex.slice(2, 4), 16);
    b = parseInt(normalizedHex.slice(4, 6), 16);
    a = parseInt(normalizedHex.slice(6, 8), 16);
  } else if (normalizedHex.length === 6) {
    r = parseInt(normalizedHex.slice(0, 2), 16);
    g = parseInt(normalizedHex.slice(2, 4), 16);
    b = parseInt(normalizedHex.slice(4, 6), 16);
    a = opacity ?? 255;
  } else {
    throw new Error('Invalid hex color format');
  }

  const alpha = a.toFixed(2); // Convert to 0–1 float with 2 decimals

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function getFillStyleWithOpacity(rgba: string, opacity?: number): string {
  if (!rgba?.startsWith('rgba')) return rgba;
  const matches = rgba.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([0-9.]+)\)/);
  if (!matches) return rgba;

  const [, r, g, b] = matches;
  const alpha = isDefined(opacity) ? opacity : matches[4];
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function extractRGB(color: string): { r: number; g: number; b: number } | null {
  // Handle rgba or rgb
  const rgbaMatch = color.match(/rgba?\(\s*(\d+),\s*(\d+),\s*(\d+)/);
  if (rgbaMatch) {
    return {
      r: parseInt(rgbaMatch[1]),
      g: parseInt(rgbaMatch[2]),
      b: parseInt(rgbaMatch[3]),
    };
  }

  // Handle hex: #RRGGBB
  if (color.startsWith('#') && color.length === 7) {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return { r, g, b };
  }

  return null; // unsupported format
}

export function extractHex(color: string): string {
  if (color.startsWith('rgba')) {
    // Extract r,g,b and convert to hex
    const rgb = extractRGB(color);
    return rgb ? rgbToHex(rgb.r, rgb.g, rgb.b) : '#000000';
  } else if (color.startsWith('#')) {
    return color;
  }
  return '#000000'; // fallback
}

export function extractOpacity(color: string): number {
  if (color.startsWith('rgba')) {
    const match = color.match(/rgba\(.+,\s*([\d.]+)\)/);
    return match ? Math.round(parseFloat(match[1]) * 100) : 100;
  }
  return 100;
}

export function rgbToHex(r: number, g: number, b: number): string {
  return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`;
}

function componentToHex(c: number): string {
  const hex = c.toString(16);
  return hex.length === 1 ? '0' + hex : hex;
}
